import React, { useMemo, useState } from "react";
import { ActiveTeamMember, CurrentCompany } from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import SettingsTable from "@/components/molecules/SettingsTable/SettingsTable";
import { SettingsFormContent, SettingsHeader } from "@/components/pages/Settings";
import { Grid } from "@mantine/core";
import CTA from "@/components/atoms/CTA";
import Text from "@/components/atoms/Text";
import RoleSelector from "@/components/organisms/RoleSelector";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteTeamMember } from "@/services/Settings";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";

interface UserTableNewProps {
  activeTeamMembers: ActiveTeamMember[];
  currentCompany: CurrentCompany | undefined;
  isAdmin: boolean;
  onClick: () => void;
}

const UserTable: React.FC<UserTableNewProps> = (props) => {
  const [userToEdit, setUserToEdit] = useState<ActiveTeamMember | null>(null);
  const { activeTeamMembers, currentCompany, isAdmin, onClick } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync: mutateDeleteUser } = useMutation({
    mutationFn: async (userId: number) => {
      await deleteTeamMember(currentCompany?.companyId as number, userId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyTeam", currentCompany?.companyId],
      });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:settings.userRemoved"))
      );
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:settings.userRemoveError"))
      );
    },
  });

  const columns: ColumnDef<ActiveTeamMember>[] = useMemo(
    () => [
      {
        header: t("common:email"),
        accessorKey: "email",
      },
      {
        header: t("common:firstname"),
        accessorKey: "firstName",
      },
      {
        header: t("common:lastname"),
        accessorKey: "lastName",
      },
      {
        header: t("common:role"),
        accessorKey: "roleType",
        cell: ({ row }) => {
          if (userToEdit && userToEdit.id === row.original.id) {
            return (
              <RoleSelector
                userId={row.original.id}
                currentRole={row.original.roleType}
                currentCompany={currentCompany}
                disabled={!isAdmin}
                onClose={() => setUserToEdit(null)}
              />
            );
          } else {
            const role = t(`enumerables:userRoles.${row.original.roleType}`);
            return <span>{role}</span>;
          }
        },
      },
    ],
    [t, currentCompany, isAdmin, userToEdit]
  );

  const editActions = (user: ActiveTeamMember) => [
    {
      label: t("settings:edit"),
      action: () => setUserToEdit(user),
    },
    {
      label: t("settings:delete"),
      action: async () => mutateDeleteUser(user.id),
    },
  ];

  return (
    <React.Fragment>
      <SettingsFormContent>
        <Grid>
          <SettingsHeader showDivider={false}>
            <Text size="md" strong={true} display="technical" tt="uppercase">
              {t("settings:activeUsers")}
            </Text>
            {isAdmin && (
              <CTA id="settings-team-add" intent="secondary" onClick={onClick}>
                {t("settings:newUser")}
              </CTA>
            )}
          </SettingsHeader>
          <Grid.Col span={12}>
            <SettingsTable data={activeTeamMembers} columns={columns} editActions={editActions} isAdmin={isAdmin} />
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
    </React.Fragment>
  );
};

export default UserTable;
